/**
 * Promotion — « **La Découpe** » : le pattern et le barème (SPEC-UI §5.5, re-expliqué par
 * l'avenant **A-3**, verbe *parier*).
 *
 * Ta vidéo terminée est une barre horizontale. Tu poses des **fenêtres d'extrait** là où tu
 * crois que les meilleurs moments sont, et tu assignes chaque extrait à une plateforme. Les
 * vrais moments chauds sont **cachés** — mais tu as deux indices, et c'est là que l'avenant
 * change tout :
 *
 * 1. **la forme d'onde audio**, dont les pics corrèlent avec les zones chaudes… imparfaitement ;
 * 2. **tes réussites des étapes précédentes laissent des marqueurs** — « un cut parfait au
 *    Montage = un marqueur visible ici ». La boucle se referme entre les cinq jeux : bien
 *    jouer le début de l'Atelier rend la fin lisible.
 *
 * Plateformes parodiques (CLAUDE.md : jamais de marque réelle) : **TokTok** (humour),
 * **InstaPix** (wow), **MeTube Shorts** (info).
 */

import {
  draw,
  exigenceAtelier,
  generateAtelierPattern,
  uniformFloat,
  uniformInt,
  type GameState,
} from "@maxime/shared";

import type { PatternPromotionApi, TimelinePromotion } from "../../api/atelier";

export type PlateformeId = "toktok" | "instapix" | "metube_shorts";
export type TypeMoment = "humour" | "wow" | "info";

/** Affinité plateforme ↔ type de moment (§5.5 point 3). */
export const AFFINITE: Record<PlateformeId, TypeMoment> = {
  toktok: "humour",
  instapix: "wow",
  metube_shorts: "info",
};

export const PLATEFORMES: { id: PlateformeId; nom: string; icone: string; pour: string }[] = [
  { id: "toktok", nom: "TokTok", icone: "🤣", pour: "ce qui fait rire" },
  { id: "instapix", nom: "InstaPix", icone: "✨", pour: "ce qui est beau" },
  { id: "metube_shorts", nom: "MeTube Shorts", icone: "🧠", pour: "ce qui apprend" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Constantes (SPEC-UI §4.2 et §5.5) ✎
// ─────────────────────────────────────────────────────────────────────────────

/** Chrono de base à E1, en ms (§4.2). Allongé par le mode doux et la tolérance. */
export const CHRONO_BASE_MS = 20_000;
/** Zones chaudes à E1 (§4.2). */
export const ZONES_BASE = 3;
/** Longueur d'un extrait, en fraction de la timeline (§4.2 : 10 %). */
export const LONGUEUR_EXTRAIT = 0.1;
/**
 * Largeur d'une zone chaude. SPEC-UI §5.5 propose ✎ `[0,06 ; 0,12]` — mais un extrait fait
 * 10 % : **une zone de 12 % ne peut pas être couverte entièrement**, et comme `C_max` du
 * barème suppose un recouvrement de 1 par zone, le score 100 devenait **inatteignable** quel
 * que soit le talent du joueur. Trouvé par `npm run verif`.
 *
 * On borne donc la largeur à la longueur d'un extrait. C'est un réglage de **génération**,
 * pas une retouche du barème (celui-ci reste celui de la spec, à la virgule) — et l'avenant
 * A-3 fait justement de « zones chaudes plus courtes » son axe de scaling. À signaler quand
 * même : §5.5 porte encore le 0,12.
 */
export const ZONE_LARGEUR_MIN = 0.06;
export const ZONE_LARGEUR_MAX = LONGUEUR_EXTRAIT;
/** Extraits posables : 3, ou 4 avec *Merch* / *L'agent* (§5.5). */
export const EXTRAITS_BASE = 3;
/** Coût d'un extrait posé, en énergie (0 avec *Usine à shorts*). */
export const ENERGIE_PAR_EXTRAIT = 2;
/** Échantillons de la forme d'onde affichée. */
export const ECHANTILLONS_ONDE = 64;
/** Score d'étape moyen à partir duquel un marqueur se gagne ⟦A-3⟧. */
export const SEUIL_MARQUEUR = 25;

export interface ZoneChaude {
  debut: number;
  largeur: number;
  /** 0,5 · 0,7 · 1,0 (§5.5). */
  intensite: number;
  type: TypeMoment;
}

// ─────────────────────────────────────────────────────────────────────────────
// ⟦A-69⟧ « Le métrage » — jumeau NON AUTORITATIF du serveur
// ─────────────────────────────────────────────────────────────────────────────
// Jumeau de `server/src/atelier/constantes.ts` (bloc `METRAGE_*`) et de
// `server/src/atelier/scoring.ts#{longueursApresMetrage,noterMetrage}`. Comme partout ici : le
// serveur note, ce module ne sert qu'à ce que l'écran dise la même chose que lui.

export const METRAGE_CHRONO_BASE_MS = 10_000;
export const METRAGE_PLANCHER = 0.1;
export const METRAGE_MIN_MULT = 0.45;
export const METRAGE_MAX_MULT = 1.85;
export const METRAGE_POIDS = 0.25;

/** La phase de répartition, ou `null` quand le nœud n'est pas acheté. */
export interface Metrage {
  chronoMs: number;
  budget: number;
  min: number;
  max: number;
  departs: number[];
}

export interface PatternDecoupe {
  chronoMs: number;
  extraits: number;
  /** La longueur **par défaut** d'un extrait — le scalaire reste la valeur de repli. */
  longueur: number;
  /** ⟦A-69⟧ La phase de répartition, ou `null` : c'est le seul interrupteur. */
  metrage: Metrage | null;
  energieParExtrait: number;
  /**
   * La vérité, cachée jusqu'à la validation — et **`null` en mode compte**.
   *
   * C'est le seul secret que le serveur garde vraiment : `patternsPublics()` retire la heatmap
   * parce que « la connaître, c'est faire 100 à coup sûr ». Conséquence honnête et non
   * contournée : côté compte, le client **ne peut ni noter cette étape ni la révéler**. Il ne
   * fabrique pas un chiffre pour combler le trou (ce serait exactement le mensonge que
   * `gameClient.ts` interdit) — il le dit, et il attend le serveur.
   */
  zones: ZoneChaude[] | null;
  /** Zones révélées d'entrée par un assist (*Networking* : les intensités 1,0). */
  revelees: ZoneChaude[];
  /** *Analytics addict* : toutes les zones clignotent. */
  clignotent: boolean;
  /** ⟦A-3⟧ Marqueurs gagnés aux étapes précédentes : positions de zones, offertes. */
  marqueurs: number[];
  /** Forme d'onde : indice bruité, ses pics corrèlent imparfaitement avec les zones. */
  onde: number[];
  scoreCap: number;
  k: number;
  /** ⟦A-83⟧ Mode posé (SPEC-UI §7.4) : « chrono retiré (soumission manuelle uniquement) ». */
  pose: boolean;
}

export interface OptionsDecoupe {
  state: GameState;
  seed: string;
  /** Scores déjà obtenus dans cet Atelier — ils achètent les marqueurs ⟦A-3⟧. */
  scoresPrecedents: readonly number[];
  /** *Merch* / *L'agent* : un extrait de plus. */
  extraitBonus?: boolean;
  /** *Networking* : les zones d'intensité 1,0 sont visibles d'entrée. */
  networking?: boolean;
  /** *Analytics addict* : toutes les zones clignotent. */
  analytics?: boolean;
  /** *Usine à shorts* : les extraits ne coûtent rien. */
  usineAShorts?: boolean;
  /** ⟦A-69⟧ *Usine à shorts* : la phase « Le métrage » précède la découpe. */
  phaseMetrage?: boolean;
  /** ⟦A-83⟧ `prefers-reduced-motion` : la variante posée (§7.4) — jumelle du serveur. */
  pose?: boolean;
}

/**
 * ⟦A-83 · SPEC-UI §7.4⟧ Mode posé : « chrono retiré ». Le chrono est **triplé** côté pattern
 * (le validateur borne encore la durée d'une soumission), jamais affiché, jamais tranchant.
 * Jumeau de `POSE_CHRONO_MULT_SANS_TEMPS` côté serveur.
 */
export const POSE_CHRONO_MULT = 3;

/** Le métrage du bac à sable — jumeau de `GenerateurPatterns#metrage`. */
function metrageLocal(extraits: number, longueur: number, tempoMult: number, pose = false): Metrage {
  const budget = longueur * extraits * (1 + METRAGE_PLANCHER);
  return {
    chronoMs: Math.max(4_500, Math.round(METRAGE_CHRONO_BASE_MS / tempoMult)) * (pose ? POSE_CHRONO_MULT : 1),
    budget,
    min: longueur * METRAGE_MIN_MULT,
    max: longueur * METRAGE_MAX_MULT,
    departs: Array.from({ length: extraits }, () => (extraits > 0 ? budget / extraits : 0)),
  };
}

export function genererDecoupe(options: OptionsDecoupe): PatternDecoupe {
  const { state, seed } = options;
  const exigence = exigenceAtelier(state);
  const graine = generateAtelierPattern(state, "promotion", seed).seed_component;
  const u = (i: number) => draw(graine, 0, "atelier_pattern", i);

  const combien = Math.max(2, Math.round(ZONES_BASE * exigence.cuts_mult));
  const zones = poserZones(combien, u);

  // ⟦A-3⟧ « tes réussites des étapes précédentes laissent des marqueurs » : un marqueur par
  // tranche de SEUIL_MARQUEUR points de moyenne. Bien jouer le début rend la fin lisible.
  const moyenne =
    options.scoresPrecedents.length === 0
      ? 0
      : options.scoresPrecedents.reduce((a, b) => a + b, 0) / options.scoresPrecedents.length;
  const nbMarqueurs = Math.min(zones.length, Math.floor(moyenne / SEUIL_MARQUEUR));
  const marqueurs = [...zones]
    .sort((a, b) => b.intensite - a.intensite)
    .slice(0, nbMarqueurs)
    .map((z) => z.debut + z.largeur / 2);

  const extraits = EXTRAITS_BASE + (options.extraitBonus ? 1 : 0);

  const pose = options.pose === true;
  return {
    chronoMs: Math.round(CHRONO_BASE_MS * exigence.window_mult) * (pose ? POSE_CHRONO_MULT : 1),
    extraits,
    longueur: LONGUEUR_EXTRAIT,
    metrage: options.phaseMetrage
      ? metrageLocal(extraits, LONGUEUR_EXTRAIT, exigence.tempo_mult, pose)
      : null,
    energieParExtrait: options.usineAShorts ? 0 : ENERGIE_PAR_EXTRAIT,
    zones,
    revelees: options.analytics ? zones : options.networking ? zones.filter((z) => z.intensite >= 1) : [],
    clignotent: options.analytics === true,
    marqueurs,
    onde: genererOnde(zones, u),
    scoreCap: exigence.score_cap,
    k: exigence.k,
    pose,
  };
}

/**
 * Le pattern **servi par le serveur**, traduit tel quel — **sans la heatmap**, qui ne descend
 * jamais du fil. Tout le reste (l'onde, les marqueurs gagnés, les zones qu'un assist a payées)
 * est là : le jeu se joue entier, seul le verdict change de main.
 */
/**
 * ⚠️ **Le `scoreCap` du fil n'est PAS celui du barème d'écran.**
 *
 * L'enveloppe du serveur porte `exigenceAtelier(état, étape).score_cap`, qui **fusionne** deux
 * bornes : la tolérance d'accessibilité (85) et le **plafond de compétence** ⟦A-9⟧ de la
 * branche (55 au socle). Or le moteur applique déjà le second à la publication
 * (`scoresEffectifs`), et l'écran de l'Atelier le montre à part — « 92 · plafonné à 45, ton
 * niveau ». Le laisser raboter le score du mini-jeu, ce serait l'appliquer **deux fois** et
 * saboter le geste, ce que l'avenant interdit explicitement : « le joueur voit son vrai geste,
 * et voit ce que son niveau en retient ».
 *
 * Le barème local ne retient donc que la borne qui ne dépend de rien, lue du moteur —
 * exactement ce que fait la génération en bac à sable. Le serveur note pareil : son
 * `validate.ts` ne connaît que `TOLERANCE_SCORE_CAP`.
 */
export function depuisServeur(api: PatternPromotionApi, state: GameState): PatternDecoupe {
  return {
    chronoMs: api.params.chronoMs,
    extraits: api.params.clips,
    longueur: api.params.longueurClip,
    metrage: api.params.metrage ? { ...api.params.metrage, departs: [...api.params.metrage.departs] } : null,
    energieParExtrait: api.params.energieParClip,
    zones: null,
    revelees: api.params.zonesRevelees.map((z) => ({ ...z })),
    clignotent: api.params.zonesClignotent,
    marqueurs: [...api.params.marqueurs],
    onde: [...api.params.onde],
    scoreCap: exigenceAtelier(state).score_cap,
    k: api.tier,
    // ⟦A-83⟧ Le serveur a généré la variante posée si on la lui a demandée (§7.4).
    pose: api.reducedMotion,
  };
}

/** `n` zones sans chevauchement, marge ≥ 3 % (§5.5), largeurs 6-12 %, intensités fermées. */
function poserZones(n: number, u: (i: number) => number): ZoneChaude[] {
  const zones: ZoneChaude[] = [];
  const intensites = [0.5, 0.7, 1.0] as const;
  const types: TypeMoment[] = ["humour", "wow", "info"];
  // On découpe la timeline en `n` couloirs et on pose une zone dans chacun : sans
  // chevauchement par construction, et réparti sur toute la vidéo.
  const couloir = 1 / n;
  for (let i = 0; i < n; i++) {
    const largeur = uniformFloat(u(i), ZONE_LARGEUR_MIN, ZONE_LARGEUR_MAX);
    const marge = Math.max(0, couloir - largeur - 0.03);
    const debut = i * couloir + uniformFloat(u(20 + i), 0, marge);
    zones.push({
      debut: Math.min(1 - largeur, debut),
      largeur,
      intensite: intensites[uniformInt(u(40 + i), 0, 2)]!,
      type: types[uniformInt(u(60 + i), 0, 2)]!,
    });
  }
  return zones;
}

/**
 * La forme d'onde : un indice **imparfait**. On part d'un bruit seedé, et on gonfle les
 * échantillons proches d'une zone chaude — mais pas assez pour que lire l'onde suffise.
 */
function genererOnde(zones: readonly ZoneChaude[], u: (i: number) => number): number[] {
  return Array.from({ length: ECHANTILLONS_ONDE }, (_, i) => {
    const position = (i + 0.5) / ECHANTILLONS_ONDE;
    const zone = zones.find((z) => position >= z.debut && position <= z.debut + z.largeur);
    const bruit = uniformFloat(u(100 + i), 0.15, 0.6);
    const renfort = zone ? zone.intensite * uniformFloat(u(200 + i), 0.2, 0.5) : 0;
    return Math.min(1, bruit + renfort);
  });
}

export interface Extrait {
  /** Début de l'extrait sur la timeline, en fraction. */
  debut: number;
  plateforme: PlateformeId;
}

export interface ResultatDecoupe {
  score: number;
  /** Couverture des zones chaudes, 0-1. */
  couverture: number;
  /** Part des extraits sur la bonne plateforme, 0-1. */
  matching: number;
  /** ⟦A-69⟧ Le score de la découpe seule, avant composition — `null` sans la phase. */
  decoupe: number | null;
  /** ⟦A-69⟧ La note du métrage, ou `null` sans la phase. */
  metrage: NoteMetrage | null;
  /** Ce que chaque extrait a effectivement attrapé — pour la révélation. */
  detail: { extrait: Extrait; zone: ZoneChaude | null; recouvrement: number; bonnePlateforme: boolean }[];
}

/** ⟦A-69⟧ Ce qu'un extrait a coûté en métrage, et ce que ce métrage a attrapé. */
export interface Taille {
  longueur: number;
  /** 0-1 : part de la longueur tombée dans une zone chaude. */
  utile: number;
}

export interface NoteMetrage {
  score: number;
  tailles: Taille[];
}

/**
 * ⟦A-69⟧ Les longueurs finales — jumeau de `scoring.ts#longueursApresMetrage`.
 *
 * Rend `null` sans la phase : les appelants retombent alors sur `pattern.longueur`, **le même
 * nombre**. Bornage puis renormalisation au prorata : la somme est **fermée**, dépasser le
 * budget ne peut jamais rapporter.
 */
export function longueursApresMetrage(
  pattern: PatternDecoupe,
  soumis: TimelinePromotion["metrage"],
): number[] | null {
  const plan = pattern.metrage;
  if (!plan) return null;

  const fin = Math.max(0, Math.min(plan.chronoMs, soumis?.finishedAtMs ?? 0));
  const poses = [...(soumis?.poses ?? [])].sort((a, b) => a.atMs - b.atMs);

  const longueurs = plan.departs.map((depart, i) => {
    let longueur = depart;
    for (const p of poses) {
      if (p.clip !== i || p.atMs > fin) continue;
      longueur = p.longueur;
    }
    return Math.max(plan.min, Math.min(plan.max, longueur));
  });

  const somme = longueurs.reduce((t, v) => t + v, 0);
  if (somme > plan.budget && somme > 0) {
    const facteur = plan.budget / somme;
    return longueurs.map((v) => v * facteur);
  }
  return longueurs;
}

/**
 * ⟦A-69⟧ La note du métrage — jumeau de `scoring.ts#noterMetrage`, à la virgule.
 *
 * `note(extrait) = 100 × (métrage tombé dans le chaud) / (métrage dépensé)`. **Du temps mort
 * dans un short, ça se voit.**
 */
export function noterMetrage(
  pattern: PatternDecoupe,
  longueurs: number[] | null,
  extraits: readonly Extrait[],
): NoteMetrage | null {
  if (!pattern.metrage || longueurs === null || pattern.zones === null) return null;
  const zones = pattern.zones;

  const tailles: Taille[] = longueurs.map((longueur, i) => {
    const extrait = extraits[i];
    if (extrait === undefined || longueur <= 0) return { longueur, utile: 0 };
    const debut = extrait.debut;
    const fin = debut + longueur;
    const bouts = zones
      .map((z) => ({ d: Math.max(debut, z.debut), f: Math.min(fin, z.debut + z.largeur) }))
      .filter((b) => b.f > b.d)
      .sort((a, b) => a.d - b.d);
    let chaud = 0;
    let couvertJusqua = debut;
    for (const b of bouts) {
      const d = Math.max(b.d, couvertJusqua);
      if (b.f > d) {
        chaud += b.f - d;
        couvertJusqua = b.f;
      }
    }
    return { longueur, utile: Math.max(0, Math.min(1, chaud / longueur)) };
  });

  const score =
    tailles.length === 0 ? 0 : (100 * tailles.reduce((s, t) => s + t.utile, 0)) / tailles.length;
  return { score: Math.max(0, Math.min(100, score)), tailles };
}

/**
 * Barème §5.5, recopié :
 *
 * ```
 * overlap(i,j) = longueur(clip_i ∩ zone_j) / width_j
 * C     = Σ_zones intensity_j × max_i overlap(i,j)
 * C_max = Σ des intensités des min(n, clipCount) zones les plus intenses
 * M     = (clips dont la plateforme = type de la zone la plus recouverte) / clipCount
 * score = clamp(0, 100, 70 × C / C_max + 30 × M)
 * ```
 */
export function noterDecoupe(
  pattern: PatternDecoupe,
  extraits: readonly Extrait[],
  longueurs: number[] | null = null,
): ResultatDecoupe | null {
  // Pas de heatmap ⇒ rien à noter. Le serveur, lui, l'a (§6.4 pas 3).
  if (pattern.zones === null) return null;
  const zones = pattern.zones;
  // ⟦A-69 · A-59 pt 4⟧ Le scalaire reste la valeur de repli : sans métrage, pas une
  // multiplication de plus, pas un arrondi de plus.
  const longueurDe = (i: number): number => longueurs?.[i] ?? pattern.longueur;
  const recouvrement = (extrait: Extrait, longueur: number, zone: ZoneChaude) => {
    const debut = Math.max(extrait.debut, zone.debut);
    const fin = Math.min(extrait.debut + longueur, zone.debut + zone.largeur);
    return Math.max(0, fin - debut) / zone.largeur;
  };

  let c = 0;
  for (const zone of zones) {
    let meilleur = 0;
    extraits.forEach((extrait, i) => {
      meilleur = Math.max(meilleur, recouvrement(extrait, longueurDe(i), zone));
    });
    c += zone.intensite * meilleur;
  }
  const cMax = [...zones]
    .sort((a, b) => b.intensite - a.intensite)
    .slice(0, Math.min(zones.length, pattern.extraits))
    .reduce((total, z) => total + z.intensite, 0);

  const detail = extraits.map((extrait, i) => {
    let zone: ZoneChaude | null = null;
    let meilleur = 0;
    for (const z of zones) {
      const r = recouvrement(extrait, longueurDe(i), z);
      if (r > meilleur) {
        meilleur = r;
        zone = z;
      }
    }
    return {
      extrait,
      zone,
      recouvrement: meilleur,
      bonnePlateforme: zone !== null && meilleur > 0 && AFFINITE[extrait.plateforme] === zone.type,
    };
  });

  const matching =
    pattern.extraits === 0 ? 0 : detail.filter((d) => d.bonnePlateforme).length / pattern.extraits;
  const couverture = cMax > 0 ? c / cMax : 0;
  const decoupe = Math.max(0, Math.min(100, 70 * couverture + 30 * matching));

  // ⟦A-69⟧ Sans métrage (`plan === null`), la ligne suivante rend `decoupe` — au centième près.
  const plan = noterMetrage(pattern, longueurs, extraits);
  const brut = plan === null ? decoupe : (1 - METRAGE_POIDS) * decoupe + METRAGE_POIDS * plan.score;

  return {
    score: Math.max(0, Math.min(pattern.scoreCap, Math.round(brut))),
    couverture,
    matching,
    decoupe: plan === null ? null : decoupe,
    metrage: plan,
    detail,
  };
}

/**
 * Position valide la plus proche pour un nouvel extrait : dans la timeline, et sans
 * chevaucher un extrait déjà posé (§5.5 : « le drop le plus proche valide est proposé »).
 * Rend `null` s'il n'y a plus de place.
 */
export function positionValide(
  pattern: PatternDecoupe,
  souhaite: number,
  poses: readonly Extrait[],
  longueurs: number[] | null = null,
): number | null {
  // ⟦A-69⟧ La longueur du clip qu'on est en train de poser est celle de son rang — le prochain.
  // Sans métrage, `longueurs` vaut `null` et tout retombe sur le scalaire d'origine.
  const mienne = longueurs?.[poses.length] ?? pattern.longueur;
  const max = 1 - mienne;
  const chevauche = (debut: number) =>
    poses.some((e, i) => {
      const sienne = longueurs?.[i] ?? pattern.longueur;
      return debut < e.debut + sienne && e.debut < debut + mienne;
    });

  const depart = Math.max(0, Math.min(max, souhaite));
  if (!chevauche(depart)) return depart;

  // On s'écarte par pas de 1 %, des deux côtés, jusqu'à trouver un trou.
  for (let ecart = 0.01; ecart <= 1; ecart += 0.01) {
    for (const candidat of [depart - ecart, depart + ecart]) {
      if (candidat < 0 || candidat > max) continue;
      if (!chevauche(candidat)) return candidat;
    }
  }
  return null;
}
